package com.example.event_management_system.ui.screens.events

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.DateRange
import androidx.compose.material.icons.filled.Schedule
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExposedDropdownMenuBox
import androidx.compose.material3.ExposedDropdownMenuDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.MenuAnchorType
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Snackbar
import androidx.compose.material3.SnackbarDuration
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TimeInput
import androidx.compose.runtime.key
import androidx.compose.material3.rememberTimePickerState
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.example.event_management_system.data.models.EventStatus
import com.example.event_management_system.ui.components.common.CustomDatePickerDialog
import com.example.event_management_system.ui.components.common.LoadingIndicator
import com.example.event_management_system.ui.components.common.SmallLoadingIndicator
import com.example.event_management_system.ui.theme.Dimens
import event_management_system.composeapp.generated.resources.Res
import event_management_system.composeapp.generated.resources.*
import kotlin.time.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun EventFormDialog(
    prefilledDate: Long? = null,
    initialLocationId: String? = null,
    onDismiss: () -> Unit,
    onSaved: () -> Unit
) {
    val screenModel = koinInject<EventFormScreenModel>()
    val state by screenModel.state.collectAsState()
    val snackbarHostState = remember { SnackbarHostState() }
    var isSuccessSnackbar by remember { mutableStateOf(false) }
    var showSectionDropdown by remember { mutableStateOf(false) }
    var showStatusDropdown by remember { mutableStateOf(false) }
    var showDatePicker by remember { mutableStateOf(false) }
    var showTimePicker by remember { mutableStateOf(false) }
    var showEndTimePicker by remember { mutableStateOf(false) }

    val (initialHour, initialMinute) = parseDialogTimeToComponents(state.eventTime)
    val timePickerState = key(state.eventTime) {
        rememberTimePickerState(initialHour = initialHour, initialMinute = initialMinute, is24Hour = false)
    }
    val (initialEndHour, initialEndMinute) = parseDialogTimeToComponents(state.eventEndTime)
    val endTimePickerState = key(state.eventEndTime) {
        rememberTimePickerState(initialHour = initialEndHour, initialMinute = initialEndMinute, is24Hour = false)
    }

    DisposableEffect(Unit) {
        screenModel.initialize(null, prefilledDate, initialLocationId)
        onDispose { }
    }

    LaunchedEffect(state.isSaved) {
        if (state.isSaved) onSaved()
    }

    LaunchedEffect(state.emailSentSuccess) {
        if (state.emailSentSuccess) {
            isSuccessSnackbar = true
            snackbarHostState.showSnackbar(
                message = "Email sent successfully!",
                duration = SnackbarDuration.Short
            )
            onSaved()
        }
    }

    LaunchedEffect(state.error) {
        state.error?.let { error ->
            isSuccessSnackbar = false
            snackbarHostState.showSnackbar(error)
            screenModel.clearError()
        }
    }

    Dialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(usePlatformDefaultWidth = false)
    ) {
        Surface(
            modifier = Modifier
                .fillMaxWidth(0.45f)
                .heightIn(max = 800.dp),
            shape = RoundedCornerShape(16.dp),
            color = MaterialTheme.colorScheme.surface,
            shadowElevation = 8.dp
        ) {
            Box {
                Column(modifier = Modifier.fillMaxWidth()) {
                    // Header
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 24.dp, vertical = 16.dp),
                        horizontalArrangement = Arrangement.SpaceBetween,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text(
                            text = stringResource(Res.string.screen_title_new_event),
                            style = MaterialTheme.typography.titleLarge,
                            fontWeight = FontWeight.SemiBold
                        )
                        IconButton(onClick = onDismiss) {
                            Icon(Icons.Default.Close, contentDescription = "Close")
                        }
                    }

                    HorizontalDivider()

                    if (state.isLoading) {
                        Box(
                            modifier = Modifier.fillMaxWidth().height(300.dp),
                            contentAlignment = Alignment.Center
                        ) {
                            LoadingIndicator()
                        }
                    } else {
                        Column(
                            modifier = Modifier
                                .weight(1f, fill = false)
                                .verticalScroll(rememberScrollState())
                                .padding(horizontal = 24.dp, vertical = 16.dp),
                            verticalArrangement = Arrangement.spacedBy(Dimens.spacingLarge)
                        ) {
                            // Customer Info
                            Text(
                                text = stringResource(Res.string.section_customer_info),
                                style = MaterialTheme.typography.titleMedium,
                                color = MaterialTheme.colorScheme.primary
                            )

                            OutlinedTextField(
                                value = state.customerName,
                                onValueChange = screenModel::onCustomerNameChange,
                                label = { Text(stringResource(Res.string.label_customer_name)) },
                                modifier = Modifier.fillMaxWidth(),
                                singleLine = true,
                                isError = state.validationErrors.containsKey("customerName"),
                                supportingText = state.validationErrors["customerName"]?.let {
                                    { Text(it, color = MaterialTheme.colorScheme.error) }
                                },
                                enabled = !state.isSaving
                            )

                            OutlinedTextField(
                                value = state.customerEmail,
                                onValueChange = screenModel::onCustomerEmailChange,
                                label = { Text(stringResource(Res.string.label_email_required)) },
                                modifier = Modifier.fillMaxWidth(),
                                singleLine = true,
                                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
                                isError = state.validationErrors.containsKey("customerEmail"),
                                supportingText = state.validationErrors["customerEmail"]?.let {
                                    { Text(it, color = MaterialTheme.colorScheme.error) }
                                },
                                enabled = !state.isSaving
                            )

                            OutlinedTextField(
                                value = state.customerPhone,
                                onValueChange = screenModel::onCustomerPhoneChange,
                                label = { Text(stringResource(Res.string.label_phone_required)) },
                                modifier = Modifier.fillMaxWidth(),
                                singleLine = true,
                                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone),
                                isError = state.validationErrors.containsKey("customerPhone"),
                                supportingText = state.validationErrors["customerPhone"]?.let {
                                    { Text(it, color = MaterialTheme.colorScheme.error) }
                                },
                                enabled = !state.isSaving
                            )

                            HorizontalDivider()

                            // Event Details
                            Text(
                                text = stringResource(Res.string.section_event_details),
                                style = MaterialTheme.typography.titleMedium,
                                color = MaterialTheme.colorScheme.primary
                            )

                            // Date picker
                            Box(modifier = Modifier.fillMaxWidth()) {
                                OutlinedTextField(
                                    value = formatDialogDate(state.eventDate),
                                    onValueChange = {},
                                    label = { Text(stringResource(Res.string.label_event_date)) },
                                    modifier = Modifier.fillMaxWidth(),
                                    readOnly = true,
                                    trailingIcon = { Icon(Icons.Default.DateRange, contentDescription = null) },
                                    isError = state.validationErrors.containsKey("eventDate"),
                                    supportingText = state.validationErrors["eventDate"]?.let {
                                        { Text(it, color = MaterialTheme.colorScheme.error) }
                                    },
                                    enabled = !state.isSaving
                                )
                                Box(
                                    modifier = Modifier
                                        .matchParentSize()
                                        .clickable(enabled = !state.isSaving) { showDatePicker = true }
                                )
                            }

                            OutlinedTextField(
                                value = state.eventTime,
                                onValueChange = screenModel::onEventTimeChange,
                                label = { Text(stringResource(Res.string.label_event_time)) },
                                modifier = Modifier.fillMaxWidth(),
                                singleLine = true,
                                trailingIcon = {
                                    IconButton(
                                        onClick = { showTimePicker = true },
                                        enabled = !state.isSaving
                                    ) { Icon(Icons.Default.Schedule, contentDescription = "Pick time") }
                                },
                                isError = state.validationErrors.containsKey("eventTime"),
                                supportingText = state.validationErrors["eventTime"]?.let {
                                    { Text(it, color = MaterialTheme.colorScheme.error) }
                                },
                                enabled = !state.isSaving
                            )

                            OutlinedTextField(
                                value = state.eventEndTime,
                                onValueChange = screenModel::onEventEndTimeChange,
                                label = { Text(stringResource(Res.string.label_event_end_time)) },
                                modifier = Modifier.fillMaxWidth(),
                                singleLine = true,
                                trailingIcon = {
                                    IconButton(
                                        onClick = { showEndTimePicker = true },
                                        enabled = !state.isSaving
                                    ) { Icon(Icons.Default.Schedule, contentDescription = "Pick end time") }
                                },
                                enabled = !state.isSaving
                            )

                            OutlinedTextField(
                                value = state.guestCount,
                                onValueChange = screenModel::onGuestCountChange,
                                label = { Text(stringResource(Res.string.label_guest_count)) },
                                modifier = Modifier.fillMaxWidth(),
                                singleLine = true,
                                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                                isError = state.validationErrors.containsKey("guestCount"),
                                supportingText = state.validationErrors["guestCount"]?.let {
                                    { Text(it, color = MaterialTheme.colorScheme.error) }
                                },
                                enabled = !state.isSaving
                            )

                            OutlinedTextField(
                                value = state.cost,
                                onValueChange = screenModel::onCostChange,
                                label = { Text(stringResource(Res.string.label_event_cost)) },
                                prefix = { Text("$") },
                                modifier = Modifier.fillMaxWidth(),
                                singleLine = true,
                                enabled = !state.isSaving
                            )

                            OutlinedTextField(
                                value = state.deposit,
                                onValueChange = screenModel::onDepositChange,
                                label = { Text(stringResource(Res.string.label_deposit_dollar)) },
                                prefix = { Text("$") },
                                modifier = Modifier.fillMaxWidth(),
                                singleLine = true,
                                isError = state.validationErrors.containsKey("deposit"),
                                supportingText = state.validationErrors["deposit"]?.let {
                                    { Text(it, color = MaterialTheme.colorScheme.error) }
                                },
                                enabled = !state.isSaving
                            )

                            // Reservation Location dropdown
                            ExposedDropdownMenuBox(
                                expanded = showSectionDropdown,
                                onExpandedChange = { showSectionDropdown = it && !state.isSaving }
                            ) {
                                val selectedSection = state.sections.find { it.id == state.sectionId }
                                OutlinedTextField(
                                    value = selectedSection?.name ?: "",
                                    onValueChange = {},
                                    readOnly = true,
                                    label = { Text("Reservation Location") },
                                    trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = showSectionDropdown) },
                                    modifier = Modifier
                                        .menuAnchor(MenuAnchorType.PrimaryNotEditable)
                                        .fillMaxWidth(),
                                    enabled = !state.isSaving,
                                    placeholder = { Text("Select area (optional)") }
                                )
                                ExposedDropdownMenu(
                                    expanded = showSectionDropdown,
                                    onDismissRequest = { showSectionDropdown = false }
                                ) {
                                    if (state.sections.isEmpty()) {
                                        DropdownMenuItem(
                                            text = { Text("No areas configured", color = MaterialTheme.colorScheme.onSurfaceVariant) },
                                            onClick = { showSectionDropdown = false },
                                            contentPadding = ExposedDropdownMenuDefaults.ItemContentPadding
                                        )
                                    } else {
                                        if (state.sectionId != null) {
                                            DropdownMenuItem(
                                                text = { Text("— None —", color = MaterialTheme.colorScheme.onSurfaceVariant) },
                                                onClick = { screenModel.onSectionChange(null); showSectionDropdown = false },
                                                contentPadding = ExposedDropdownMenuDefaults.ItemContentPadding
                                            )
                                        }
                                        state.sections.forEach { section ->
                                            DropdownMenuItem(
                                                text = { Text(section.name) },
                                                onClick = { screenModel.onSectionChange(section.id); showSectionDropdown = false },
                                                contentPadding = ExposedDropdownMenuDefaults.ItemContentPadding
                                            )
                                        }
                                    }
                                }
                            }

                            HorizontalDivider()

                            // Additional Info
                            Text(
                                text = stringResource(Res.string.section_additional_info),
                                style = MaterialTheme.typography.titleMedium,
                                color = MaterialTheme.colorScheme.primary
                            )

                            OutlinedTextField(
                                value = state.eventPurpose,
                                onValueChange = screenModel::onEventPurposeChange,
                                label = { Text(stringResource(Res.string.label_event_purpose)) },
                                modifier = Modifier.fillMaxWidth(),
                                singleLine = true,
                                enabled = !state.isSaving
                            )

                            OutlinedTextField(
                                value = state.catererContact,
                                onValueChange = screenModel::onCatererContactChange,
                                label = { Text(stringResource(Res.string.label_caterer_contact)) },
                                modifier = Modifier.fillMaxWidth(),
                                singleLine = true,
                                enabled = !state.isSaving
                            )

                            OutlinedTextField(
                                value = state.notes,
                                onValueChange = screenModel::onNotesChange,
                                label = { Text(stringResource(Res.string.label_notes)) },
                                modifier = Modifier.fillMaxWidth(),
                                minLines = 3,
                                maxLines = 5,
                                enabled = !state.isSaving
                            )

                            Spacer(Modifier.height(Dimens.spacingSmall))
                        }
                    }

                    HorizontalDivider()

                    // Footer action buttons
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 24.dp, vertical = 16.dp),
                        horizontalArrangement = Arrangement.spacedBy(Dimens.spacingSmall, Alignment.End)
                    ) {
                        OutlinedButton(
                            onClick = onDismiss,
                            enabled = !state.isSaving
                        ) {
                            Text(stringResource(Res.string.action_cancel))
                        }
                        Button(
                            onClick = screenModel::save,
                            enabled = !state.isSaving
                        ) {
                            if (state.isSaving) SmallLoadingIndicator()
                            else Text(stringResource(Res.string.action_create))
                        }
                    }
                }

                // Snackbar overlay
                SnackbarHost(
                    hostState = snackbarHostState,
                    modifier = Modifier.align(Alignment.BottomCenter)
                ) { data ->
                    if (isSuccessSnackbar) {
                        Snackbar(
                            snackbarData = data,
                            containerColor = Color(0xFF16A34A),
                            contentColor = Color.White
                        )
                    } else {
                        Snackbar(snackbarData = data)
                    }
                }
            }
        }
    }

    // Time pickers
    if (showTimePicker) {
        AlertDialog(
            onDismissRequest = { showTimePicker = false },
            title = { Text(stringResource(Res.string.label_event_time)) },
            text = { TimeInput(state = timePickerState) },
            confirmButton = {
                TextButton(onClick = {
                    screenModel.onEventTimeChange(formatDialogTime(timePickerState.hour, timePickerState.minute))
                    showTimePicker = false
                }) { Text(stringResource(Res.string.action_ok)) }
            },
            dismissButton = {
                TextButton(onClick = { showTimePicker = false }) { Text(stringResource(Res.string.action_cancel)) }
            }
        )
    }

    if (showEndTimePicker) {
        AlertDialog(
            onDismissRequest = { showEndTimePicker = false },
            title = { Text(stringResource(Res.string.label_event_end_time)) },
            text = { TimeInput(state = endTimePickerState) },
            confirmButton = {
                TextButton(onClick = {
                    screenModel.onEventEndTimeChange(formatDialogTime(endTimePickerState.hour, endTimePickerState.minute))
                    showEndTimePicker = false
                }) { Text(stringResource(Res.string.action_ok)) }
            },
            dismissButton = {
                TextButton(onClick = { showEndTimePicker = false }) { Text(stringResource(Res.string.action_cancel)) }
            }
        )
    }

    if (showDatePicker) {
        CustomDatePickerDialog(
            initialDateMillis = state.eventDate,
            onConfirm = { millis ->
                screenModel.onEventDateChange(millis)
                showDatePicker = false
            },
            onDismiss = { showDatePicker = false }
        )
    }

    // Email preview dialog
    if (state.showEmailPreview) {
        AlertDialog(
            onDismissRequest = screenModel::skipEmail,
            title = { Text("Confirmation Preview") },
            text = {
                Column {
                    Text(
                        text = "To: ${state.customerEmail}",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                    Spacer(Modifier.height(Dimens.spacingMedium))
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .heightIn(max = 300.dp)
                            .verticalScroll(rememberScrollState())
                    ) {
                        Text(
                            text = state.emailDraftBody.replace("%%LOGOIMG%%", "[Logo]"),
                            style = MaterialTheme.typography.bodySmall
                        )
                    }
                }
            },
            confirmButton = {
                Button(
                    onClick = screenModel::confirmSendEmail,
                    enabled = !state.isSendingEmail
                ) { Text(if (state.isSendingEmail) "Sending…" else "Send to Host") }
            },
            dismissButton = {
                TextButton(onClick = screenModel::skipEmail) { Text("Skip") }
            }
        )
    }
}

private fun parseDialogTimeToComponents(timeStr: String): Pair<Int, Int> {
    return try {
        val trimmed = timeStr.trim()
        val colonIdx = trimmed.indexOf(':')
        val hour12 = trimmed.substring(0, colonIdx).trim().toInt()
        val rest = trimmed.substring(colonIdx + 1).trim()
        val spaceIdx = rest.indexOf(' ')
        val minute = rest.substring(0, spaceIdx).trim().toInt()
        val ampm = rest.substring(spaceIdx + 1).trim().uppercase()
        val hour24 = when {
            ampm == "PM" && hour12 != 12 -> hour12 + 12
            ampm == "AM" && hour12 == 12 -> 0
            else -> hour12
        }
        Pair(hour24.coerceIn(0, 23), minute.coerceIn(0, 59))
    } catch (_: Exception) {
        Pair(19, 0)
    }
}

private fun formatDialogTime(hour: Int, minute: Int): String {
    val ampm = if (hour < 12) "AM" else "PM"
    val displayHour = when {
        hour == 0 -> 12
        hour > 12 -> hour - 12
        else -> hour
    }
    return "$displayHour:${minute.toString().padStart(2, '0')} $ampm"
}

private fun formatDialogDate(timestamp: Long): String {
    if (timestamp == 0L) return "Select date"
    return try {
        val instant = Instant.fromEpochMilliseconds(timestamp)
        val localDateTime = instant.toLocalDateTime(TimeZone.UTC)
        val month = localDateTime.month.name.take(3).lowercase().replaceFirstChar { it.uppercase() }
        "$month ${localDateTime.dayOfMonth}, ${localDateTime.year}"
    } catch (_: Exception) {
        "Select date"
    }
}
